/**
 * CWOS "Sunrise" design tokens — single source of truth (doc 03).
 *
 * Emits (a) a typed TS object for apps, (b) CSS custom properties for Admin
 * Web (see css/tokens.css, generated to match), (c) a JSON export for tools.
 *
 * Raw hex values never appear in app code — semantic token names are the API.
 */
export const color = {
    canvas: '#F3F7F9',
    surface: '#FFFFFF',
    sunken: '#E9F1F5',
    sunken2: '#E7F0F5',
    line: '#DFE9EE',
    line2: '#C7D8E1',
    ink: '#0E2433',
    ink2: '#42606F',
    soft: '#5D6A70',
    faint: '#5C6A71',
    sky: '#00B1EC',
    skyDeep: '#00759C',
    skySoft: '#E1F5FC',
    skyMid: '#B5E7F9',
    navy: '#0F3048',
    navyDeep: '#0E283C',
    waterText: '#EAF4F8',
    waterMuted: '#C8D9E1',
    waterFaint: '#91AFBD',
    good: { base: '#3E9E6C', soft: '#E3F4EA', deep: '#2A7550' },
    look: { base: '#E09A2D', soft: '#FBF0DA', deep: '#95600D' },
    urgent: { base: '#E05A50', soft: '#FBE5E2', deep: '#B23F37' },
    unsure: { base: '#8E7CC3', soft: '#EFEBF8', deep: '#6A5A9E' },
};
export const space = {
    2: '2px',
    4: '4px',
    6: '6px',
    8: '8px',
    10: '10px',
    12: '12px',
    14: '14px',
    16: '16px',
    18: '18px',
    20: '20px',
    24: '24px',
    28: '28px',
    32: '32px',
    40: '40px',
};
export const radius = {
    sm: '8px',
    md: '12px',
    lg: '16px',
    pill: '999px',
    /**
     * Worker Waterline surface family (TS-only, like phoneFrame — the phone has
     * no CSS build). Three steps, not one: hero surfaces (clock, next visit)
     * read generous, ordinary cards read premium-but-dense, and nested wells
     * (condition boxes, inputs, attest drafts) read as interior. `phoneCard`
     * moves 13 → 16 so every existing Card lands on the standard step without
     * touching call sites.
     */
    phoneCard: '16px',
    phoneHero: '20px',
    phoneNest: '10px',
    /**
     * Worker C.1 Quick Action tile (TS-only). Between phoneCard and phoneHero on
     * purpose: a shortcut tile is friendlier than a dense card but must never
     * read as a hero surface — Current/Next work outranks it.
     */
    phoneTile: '18px',
    phoneFrame: '38px',
};
export const padding = {
    cardAdmin: '18px',
    cardMobile: '11px',
    tableCell: '12px',
};
export const shadow = {
    raised: '0 1px 2px rgba(8,28,44,.05), 0 8px 24px rgba(8,28,44,.06)',
    overlay: '0 2px 6px rgba(8,28,44,.08), 0 24px 60px rgba(8,28,44,.18)',
    scrim: 'rgba(8,28,44,.34)',
    /**
     * The default (light-surface) focus ring. CSS consumers should use the
     * `--color-focus` custom property instead, which re-points to
     * `--color-focus-water` (#00B1EC) inside the dark Water rail — no single
     * tone clears 3:1 on both. See css/tokens.css.
     */
    focus: '2px solid #00759C',
};
export const gradient = {
    hero: 'linear-gradient(115deg, #E4F4FC 0%, #F3F7F9 100%)',
    meter: 'linear-gradient(90deg, #B5E7F9, #00B1EC)',
    water: 'radial-gradient(900px 620px at 18% 8%, rgba(0,177,236,.22), transparent 55%), radial-gradient(760px 540px at 95% 78%, rgba(15,107,197,.18), transparent 52%), linear-gradient(145deg, #0E283C, #0F3048)',
    /** Reserved for surfaces where Phibian is reasoning. Never decoration. */
    intel: 'linear-gradient(115deg, #29C8F0 0%, #2E7FF0 62%, #8A7BF0 100%)',
};
/**
 * The liquid-glass material system. STATE DECIDES MATERIAL: operational facts
 * and human commitment are SOLID; Phibian's interpretation — insight,
 * evidence, recommendation, prepared action — is GLASS. FROST sits on the
 * light canvas (level B); WATER glass sits on the navy Water environment
 * (level C). Blur is a performance budget: `blurOverlay` applies only where
 * moving content passes behind a surface. Mirrors css/tokens.css exactly.
 */
export const glass = {
    frost: 'rgba(255,255,255,.62)',
    frostStrong: 'rgba(255,255,255,.78)',
    frostEdge: 'rgba(255,255,255,.85)',
    frostHighlight: 'rgba(255,255,255,.9)',
    water: 'rgba(255,255,255,.08)',
    waterStrong: 'rgba(255,255,255,.12)',
    waterEdge: 'rgba(255,255,255,.14)',
    waterHighlight: 'rgba(255,255,255,.22)',
    waterLine: 'rgba(255,255,255,.1)',
    shadowWater: '0 2px 6px rgba(4,16,26,.25), 0 24px 60px rgba(4,16,26,.35)',
    blurOverlay: '18px',
    glowIntel: '0 0 16px rgba(46,127,240,.45)',
    chipLuminous: 'rgba(0,177,236,.14)',
    chipLuminousEdge: 'rgba(0,177,236,.3)',
    chipLuminousGlow: 'rgba(0,177,236,.16)',
    /** On-Water text/status tones, pinned by water-contrast.test.ts. */
    linkWater: '#B5E7F9',
    goodWater: '#9FE6BD',
    lookWater: '#F2CF8F',
    urgentWater: '#F8C6C0',
    unsureWater: '#CFC4EC',
};
export const motion = {
    fast: '150ms',
    base: '220ms',
    ease: 'cubic-bezier(.2, 0, 0, 1)',
};
/**
 * Typography scale. Data numerals use tabular-nums via the shared CSS/theme;
 * fonts load with `font-display:swap` and system fallbacks.
 */
/**
 * Categorical chart series, mirroring `--chart-*` in css/tokens.css.
 * Separated by hue, not lightness; never the severity family. `context` is the
 * quieter baseline tone a measure is compared against, and charts pair it with
 * a dash pattern so the distinction does not rest on colour alone.
 */
export const chart = {
    1: '#0F6BC5',
    2: '#00759C',
    3: '#6B4FD8',
    4: '#0E7C8A',
    5: '#A2456D',
    context: '#6B8496',
};
/**
 * Font stacks, mirroring `--font-sans` / `--font-display` / `--font-mono` in
 * css/tokens.css. Kept in step by hand, like every other export in this file.
 */
export const font = {
    sans: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    display: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};
export const type = {
    display: {
        family: 'Geist',
        size: '26px',
        lineHeight: '34px',
        weight: 700,
        tracking: '-0.02em',
    },
    h1: { family: 'Geist', size: '24px', lineHeight: '32px', weight: 700, tracking: '-0.02em' },
    h2: { family: 'Geist', size: '17.5px', lineHeight: '24px', weight: 700, tracking: '-0.01em' },
    h3: { family: 'Geist', size: '15px', lineHeight: '21px', weight: 600, tracking: '-0.01em' },
    stat: { family: 'Geist', size: '27px', lineHeight: '33px', weight: 700, tracking: '-0.02em' },
    body: { family: 'Geist', size: '14px', lineHeight: '21.5px', weight: 450, tracking: '0' },
    secondary: { family: 'Geist', size: '12.5px', lineHeight: '17.5px', weight: 450, tracking: '0' },
    caption: { family: 'Geist', size: '11.5px', lineHeight: '16px', weight: 450, tracking: '0' },
    overline: {
        family: 'Geist',
        size: '10.5px',
        lineHeight: '14px',
        weight: 650,
        tracking: '0.07em',
    },
    button: { family: 'Geist', size: '13px', lineHeight: '18px', weight: 600, tracking: '0' },
};
/**
 * Verification badge map (doc 03 §3.5). Copy keys resolve via `@cwos/copy`.
 * `verified_inside` may render as "At a work site" or "At {geofence name}".
 */
export const verificationMap = {
    verified_inside: { variant: 'good', copyKey: 'verify.atWorkSite' },
    verified_outside: { variant: 'look', copyKey: 'verify.awayFromSites' },
    recorded_no_geofence: { variant: 'plain', copyKey: 'verify.locationNoted' },
    low_accuracy: { variant: 'unsure', copyKey: 'verify.couldntConfirm' },
    unavailable_no_fix: { variant: 'unsure', copyKey: 'verify.noReading' },
    unavailable_permission_denied: { variant: 'unsure', copyKey: 'verify.locationOff' },
    unavailable_policy_off: { variant: 'plain', copyKey: 'verify.notCollected' },
    attested_by_worker: { variant: 'unsure', copyKey: 'verify.attested' },
};
/** Confirmation badge map. No entry is `good` — see rule 1 above. */
export const evvConfirmationMap = {
    confirmed: { variant: 'sky', copyKey: 'evv.confirmed' },
    unconfirmed: { variant: 'plain', copyKey: 'evv.notConfirmed' },
};
/** Compliance badge map. `compliant` is the ONLY `good` in this file's EVV half. */
export const evvComplianceMap = {
    compliant: { variant: 'good', copyKey: 'evv.compliant' },
    noncompliant: { variant: 'look', copyKey: 'evv.noncompliant' },
    undetermined: { variant: 'unsure', copyKey: 'evv.complianceUnchecked' },
};
/** Deterministic background hue for avatar initials (from an opaque id). */
export function avatarHue(id) {
    let h = 0;
    for (let i = 0; i < id.length; i += 1)
        h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h % 360;
}
/** WCAG relative-luminance contrast ratio of two hex colors (both #RRGGBB). */
export function contrastRatio(a, b) {
    const lum = (hex) => {
        const n = hex.replace('#', '');
        const r = parseInt(n.slice(0, 2), 16) / 255;
        const g = parseInt(n.slice(2, 4), 16) / 255;
        const b2 = parseInt(n.slice(4, 6), 16) / 255;
        const f = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
        return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b2);
    };
    const l1 = lum(a);
    const l2 = lum(b);
    const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
    return (hi + 0.05) / (lo + 0.05);
}
//# sourceMappingURL=index.js.map